import React, { useState } from 'react';
import {
  ComposedChart, Area, Line, BarChart, Bar, LineChart, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import MountainProgress from './MountainProgress';

const SUBJECT_CONFIG = {
  geography:       { label: 'Geography',              color: '#58a6ff' },
  'env-science':   { label: 'Environmental Science',  color: '#3fb950' },
  psychology:      { label: 'Psychology',             color: '#f778ba' },
  biology:         { label: 'Biology',                color: '#d29922' },
  bonus:           { label: 'Milestone bonus',        color: '#a371f7' },
  other:           { label: 'Other',                  color: '#484f58' },
};

const TIER_LABELS = {
  mcqPoints:      { label: 'MCQ',              max: 12000, color: 'bg-blue-400'   },
  guided4Points:  { label: 'Guided 4-mark',    max: 2500,  color: 'bg-slate-400'  },
  guided6Points:  { label: 'Guided 6-mark',    max: 7500,  color: 'bg-sky-400'    },
  guided9Points:  { label: 'Guided 9-mark',    max: 8000,  color: 'bg-violet-400' },
  guided20Points: { label: 'Guided 20-mark',   max: 9000,  color: 'bg-rose-400'   },
  mock4Points:    { label: 'Mock 4-mark',       max: 400,   color: 'bg-amber-400'  },
  mock6Points:    { label: 'Mock 6-mark',       max: 2400,  color: 'bg-orange-400' },
  mock9Points:    { label: 'Mock 9-mark',       max: 1600,  color: 'bg-red-400'    },
  mock20Points:   { label: 'Mock 20-mark',      max: 2400,  color: 'bg-pink-400'   },
  timePoints:     { label: 'Time & Engagement', max: 7000,  color: 'bg-green-400'  },
};

const REASON_LABELS = {
  correct:         'Correct answer',
  base:            'First attempt',
  quality:         'Quality score',
  first_attempt:   'First attempt bonus',
  time_engagement: 'Active time',
  streak_bonus:    'Streak bonus',
};

const MODE_LABELS = {
  mcq:     'MCQ',
  guided:  'Guided',
  mock:    'Mock Exam',
  time:    'Time',
  streak:  'Streak',
};

// Format a Date as YYYY-MM-DD in the local timezone.
// Avoid toISOString() here: it converts to UTC, which shifts the date by one
// in any non-UTC timezone and causes per-day chart slots to look up the wrong key.
function localDateStr(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function ProgressBar({ value, max, color = 'bg-blue-400', label }) {
  const pct = Math.min(100, Math.round((value / max) * 100));
  return (
    <div className="flex items-center gap-2">
      {label && <span className="text-[10px] text-[#8b949e] font-mono w-28 flex-shrink-0">{label}</span>}
      <div className="flex-1 h-2 bg-[#21262d] rounded-full overflow-hidden">
        <div className={`h-full ${color} rounded-full transition-all duration-500`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-[10px] text-[#8b949e] font-mono w-20 text-right flex-shrink-0">
        {value.toLocaleString()} / {max.toLocaleString()}
      </span>
    </div>
  );
}

// targetLengthDays: days to the deadline — the chart x-axis ends here.
function buildChartData(dailyData, termStart, termLengthDays, targetLengthDays, termTarget = 100000) {
  if (!termStart || !termLengthDays) return [];

  const tLen = targetLengthDays || termLengthDays;
  const pointsByDate = {};
  (dailyData || []).forEach(d => { pointsByDate[d.date] = d.points; });

  const start = new Date(termStart + 'T00:00:00');
  const todayStr = localDateStr(new Date());
  const data = [];
  let cumulative = 0;

  for (let day = 0; day <= tLen; day++) {
    const date = new Date(start);
    date.setDate(start.getDate() + day);
    const dateStr = localDateStr(date);
    const isInPast = dateStr <= todayStr;

    cumulative += (pointsByDate[dateStr] || 0);
    const target = Math.round((day / tLen) * termTarget);

    data.push({
      day,
      label: date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }),
      actual: isInPast ? cumulative : null,
      target,
    });
  }

  return data;
}

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const actual = payload.find(p => p.dataKey === 'actual');
  const target = payload.find(p => p.dataKey === 'target');
  return (
    <div className="bg-[#161b22] border border-[#30363d] rounded px-3 py-2 text-xs font-mono shadow-lg">
      <div className="text-[#8b949e] mb-1">{label}</div>
      {actual?.value != null && (
        <div className="text-[#58a6ff]">Actual: {actual.value.toLocaleString()} pts</div>
      )}
      {target?.value != null && (
        <div className="text-[#d29922]">Target: {target.value.toLocaleString()} pts</div>
      )}
    </div>
  );
}

function DailyTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const pts = payload[0]?.value;
  return (
    <div className="bg-[#161b22] border border-[#30363d] rounded px-3 py-2 text-xs font-mono shadow-lg">
      <div className="text-[#8b949e] mb-1">{label}</div>
      <div className="text-[#3fb950]">{(pts || 0).toLocaleString()} pts</div>
    </div>
  );
}

// ─── Score Center Panel ────────────────────────────────────────────────────────
export function ScoreCenter({ summary, dailyData, loading, onClose, title }) {
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [dailyChartType, setDailyChartType] = useState('line'); // 'line' | 'bar'

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center bg-[#0d1117]">
        <div className="text-[#8b949e] text-sm font-mono animate-pulse">Loading score...</div>
      </div>
    );
  }

  const s = summary || {};
  const termLengthDays = s.termLengthDays || 70;
  const daysElapsed = s.daysElapsed || 1;
  const basePoints = s.totalPoints || 0;
  const bonusPoints = s.bonusPoints || 0;
  const totalPoints = basePoints + bonusPoints;
  const totalTarget = 52800;
  const pct = totalTarget > 0 ? Math.round(totalPoints / totalTarget * 100) : 0;

  const chartData = buildChartData(dailyData?.days, dailyData?.termStart, dailyData?.termLengthDays || termLengthDays, dailyData?.targetLengthDays, s.totalTarget || 100000);

  // Show only a label every ~14 days to avoid crowding
  const tickInterval = Math.max(1, Math.floor((dailyData?.termLengthDays || termLengthDays) / 5));

  return (
    <div className="flex-1 overflow-y-auto bg-[#0d1117] p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-[#e6edf3] text-lg font-semibold">{title || 'My Score'}</h2>
          <p className="text-[#8b949e] text-xs font-mono mt-1">Summer Term 2026</p>
        </div>
        <button
          onClick={onClose}
          className="text-[#8b949e] hover:text-[#e6edf3] transition-colors text-xs font-mono px-3 py-1.5 border border-[#30363d] rounded hover:border-[#58a6ff]/40"
        >
          Back
        </button>
      </div>

      {/* Main Score — clickable for breakdown */}
      <div
        className="bg-[#161b22] border border-[#30363d] rounded-lg p-5 mb-4 cursor-pointer hover:border-[#58a6ff]/40 transition-colors"
        onClick={() => setShowBreakdown(true)}
      >
        <div className="flex items-center justify-between">
          <div>
            <span className="text-[#e6edf3] text-4xl font-bold font-mono">
              {totalPoints.toLocaleString()}
            </span>
            <span className="text-[#8b949e] text-sm ml-2">pts</span>
            {bonusPoints > 0 && (
              <div className="text-[10px] text-[#8b949e] font-mono mt-1">
                {basePoints.toLocaleString()} earned
                {' + '}
                <span className="text-[#a371f7]">{bonusPoints.toLocaleString()} milestone bonus</span>
              </div>
            )}
          </div>
          <div className="text-right">
            <div className={`text-sm font-mono font-semibold ${
              s.paceStatus === 'ahead' ? 'text-[#3fb950]' :
              s.paceStatus === 'on_track' ? 'text-[#d29922]' :
              'text-[#f85149]'
            }`}>
              {s.paceStatus === 'ahead' ? 'Ahead' :
               s.paceStatus === 'on_track' ? 'On Track' : 'Behind'}
            </div>
            <div className="text-[10px] text-[#8b949e] font-mono">click for breakdown</div>
          </div>
        </div>

        <div className="mt-4">
          <div className="flex justify-between text-[10px] text-[#8b949e] font-mono mb-1">
            <span>Day {daysElapsed} of {termLengthDays}</span>
            <span>{pct}% of category caps</span>
          </div>
          <div className="relative h-3 bg-[#21262d] rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-700 bg-[#58a6ff]"
              style={{ width: `${Math.max(pct > 0 ? 2 : 0, Math.min(100, pct))}%` }}
            />
          </div>
        </div>

        <div className="mt-3 flex items-center gap-3 text-xs">
          <span className="text-[#8b949e] font-mono">Today:</span>
          <span className="text-[#e6edf3] font-mono font-semibold">{(s.todayPoints || 0).toLocaleString()} pts</span>
        </div>
      </div>

      {/* Streak widget */}
      <div className="bg-[#161b22] border border-[#30363d] rounded-lg p-4 mb-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="text-lg">{(s.currentStreakDays || 0) >= 7 ? '🔥' : '✨'}</span>
          <div>
            <span className="text-[#e6edf3] text-sm font-semibold">{s.currentStreakDays || 0}-day streak</span>
            <span className="text-[#8b949e] text-xs ml-2">Best: {s.longestStreakDays || 0} days</span>
          </div>
        </div>
        <div className="text-xs text-[#8b949e] font-mono">
          {(s.currentStreakDays || 0) >= 30 ? '+50 pts/day' :
           (s.currentStreakDays || 0) >= 14 ? '+30 pts/day' :
           (s.currentStreakDays || 0) >= 7  ? '+20 pts/day' :
           (s.currentStreakDays || 0) >= 3  ? '+10 pts/day' : 'Build a 3-day streak for bonus pts'}
        </div>
      </div>

      {/* Mont Blanc climb — animates 0 → totalPoints (100,000 = summit) */}
      <MountainProgress points={totalPoints} target={s.totalTarget || 100000} />

      {/* Points over time chart */}
      <div className="bg-[#161b22] border border-[#30363d] rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-[#e6edf3] text-sm font-semibold">Points Over Time</h3>
          <div className="flex items-center gap-4 text-[10px] font-mono text-[#8b949e]">
            <span className="flex items-center gap-1.5">
              <span className="inline-block w-3 h-0.5 bg-[#58a6ff] rounded" />
              Actual
            </span>
            <span className="flex items-center gap-1.5">
              <span className="inline-block w-3 h-0.5 bg-[#d29922] rounded" style={{ borderStyle: 'dashed' }} />
              Target (100k)
            </span>
          </div>
        </div>
        {chartData.length > 0 ? (
          <ResponsiveContainer width="100%" height={200}>
            <ComposedChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="actualGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#58a6ff" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#58a6ff" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#21262d" vertical={false} />
              <XAxis
                dataKey="label"
                tick={{ fill: '#484f58', fontSize: 10, fontFamily: 'monospace' }}
                tickLine={false}
                axisLine={{ stroke: '#30363d' }}
                interval={tickInterval - 1}
              />
              <YAxis
                tick={{ fill: '#484f58', fontSize: 10, fontFamily: 'monospace' }}
                tickLine={false}
                axisLine={false}
                tickFormatter={v => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}
                width={36}
              />
              <Tooltip content={<ChartTooltip />} />
              <Area
                type="monotone"
                dataKey="actual"
                stroke="#58a6ff"
                strokeWidth={2}
                fill="url(#actualGradient)"
                dot={false}
                connectNulls={false}
                isAnimationActive={false}
              />
              <Line
                type="monotone"
                dataKey="target"
                stroke="#d29922"
                strokeWidth={1.5}
                strokeDasharray="4 3"
                dot={false}
                isAnimationActive={false}
              />
            </ComposedChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-[200px] flex items-center justify-center text-[#484f58] text-xs font-mono">
            No data yet
          </div>
        )}
      </div>

      {/* Daily points chart (bar or line) */}
      {dailyData?.termStart && (() => {
        const pointsByDate = {};
        (dailyData.days || []).forEach(d => { pointsByDate[d.date] = d.points; });
        const userMax = Math.max(0, ...(dailyData.days || []).map(d => d.points));
        const DAILY_CAP = Math.max(3000, userMax);

        // Collect subjects present in the data
        const subjectSet = new Set();
        (dailyData.bySubject || []).forEach(r => subjectSet.add(r.subject ?? 'other'));
        const subjects = [...subjectSet].filter(s => s !== 'other');
        if (subjectSet.has('other')) subjects.push('other');

        // Build per-subject lookup: { [date]: { [subject]: points } }
        const subjectByDate = {};
        (dailyData.bySubject || []).forEach(r => {
          const key = r.subject ?? 'other';
          if (!subjectByDate[r.date]) subjectByDate[r.date] = {};
          subjectByDate[r.date][key] = (subjectByDate[r.date][key] || 0) + Number(r.points);
        });

        // Fill every day from term start to today
        const start = new Date(dailyData.termStart + 'T00:00:00');
        const todayStr = localDateStr(new Date());
        const termDays = dailyData.termLengthDays || s.termLengthDays || 70;
        const allDays = [];
        for (let day = 0; day <= termDays; day++) {
          const date = new Date(start);
          date.setDate(start.getDate() + day);
          const dateStr = localDateStr(date);
          if (dateStr > todayStr) break;
          const raw = pointsByDate[dateStr] || 0;
          const entry = {
            label: date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }),
            points: Math.min(raw, DAILY_CAP),
            capped: raw > DAILY_CAP,
          };
          subjects.forEach(subj => {
            entry[subj] = subjectByDate[dateStr]?.[subj] || 0;
          });
          allDays.push(entry);
        }

        const tickInterval = Math.max(0, Math.floor(allDays.length / 8) - 1);
        const xAxis = (
          <XAxis
            dataKey="label"
            tick={{ fill: '#484f58', fontSize: 10, fontFamily: 'monospace' }}
            tickLine={false}
            axisLine={{ stroke: '#30363d' }}
            interval={tickInterval}
          />
        );
        const yAxis = (
          <YAxis
            domain={[0, DAILY_CAP]}
            tick={{ fill: '#484f58', fontSize: 10, fontFamily: 'monospace' }}
            tickLine={false}
            axisLine={false}
            tickFormatter={v => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}
            width={36}
          />
        );

        return (
          <div className="bg-[#161b22] border border-[#30363d] rounded-lg p-4 mt-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-[#e6edf3] text-sm font-semibold">Points Per Day</h3>
              <div className="flex items-center gap-3">
                <span className="text-[10px] text-[#484f58] font-mono">cap: {DAILY_CAP.toLocaleString()}</span>
                <div className="flex rounded overflow-hidden border border-[#30363d]">
                  <button
                    onClick={() => setDailyChartType('line')}
                    className={`text-[10px] px-2 py-0.5 font-mono transition-colors ${
                      dailyChartType === 'line'
                        ? 'bg-[#58a6ff] text-[#0d1117] font-semibold'
                        : 'text-[#8b949e] hover:text-[#e6edf3]'
                    }`}
                  >
                    Line
                  </button>
                  <button
                    onClick={() => setDailyChartType('bar')}
                    className={`text-[10px] px-2 py-0.5 font-mono transition-colors ${
                      dailyChartType === 'bar'
                        ? 'bg-[#58a6ff] text-[#0d1117] font-semibold'
                        : 'text-[#8b949e] hover:text-[#e6edf3]'
                    }`}
                  >
                    Bar
                  </button>
                </div>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={subjects.length > 1 ? 190 : 160}>
              {dailyChartType === 'bar' ? (
                <BarChart data={allDays} margin={{ top: 4, right: 8, left: 0, bottom: 0 }} barCategoryGap="20%">
                  <CartesianGrid strokeDasharray="3 3" stroke="#21262d" vertical={false} />
                  {xAxis}
                  {yAxis}
                  <Tooltip
                    cursor={{ fill: '#21262d' }}
                    content={({ active, payload, label }) => {
                      if (!active || !payload?.length) return null;
                      const total = payload.reduce((sum, p) => sum + (p.value || 0), 0);
                      return (
                        <div className="bg-[#161b22] border border-[#30363d] rounded px-3 py-2 text-xs font-mono shadow-lg">
                          <div className="text-[#8b949e] mb-1">{label}</div>
                          {payload.map(p => p.value > 0 && (
                            <div key={p.dataKey} style={{ color: p.fill }}>{(SUBJECT_CONFIG[p.dataKey] || SUBJECT_CONFIG.other).label}: {p.value.toLocaleString()} pts</div>
                          ))}
                          {payload.length > 1 && <div className="text-[#e6edf3] mt-1 border-t border-[#30363d] pt-1">Total: {total.toLocaleString()} pts</div>}
                        </div>
                      );
                    }}
                  />
                  {subjects.length > 1 && (
                    <Legend
                      formatter={val => <span style={{ color: '#8b949e', fontSize: 10, fontFamily: 'monospace' }}>{(SUBJECT_CONFIG[val] || SUBJECT_CONFIG.other).label}</span>}
                      wrapperStyle={{ paddingTop: 8 }}
                    />
                  )}
                  {subjects.map((subj, i) => (
                    <Bar
                      key={subj}
                      dataKey={subj}
                      stackId="a"
                      fill={(SUBJECT_CONFIG[subj] || SUBJECT_CONFIG.other).color}
                      fillOpacity={0.85}
                      radius={i === subjects.length - 1 ? [2, 2, 0, 0] : [0, 0, 0, 0]}
                      isAnimationActive={false}
                    />
                  ))}
                </BarChart>
              ) : (
                <LineChart data={allDays} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#21262d" vertical={false} />
                  {xAxis}
                  {yAxis}
                  <Tooltip content={<DailyTooltip />} />
                  <Line
                    type="monotone"
                    dataKey="points"
                    stroke="#3fb950"
                    strokeWidth={1.5}
                    dot={false}
                    isAnimationActive={false}
                  />
                </LineChart>
              )}
            </ResponsiveContainer>
          </div>
        );
      })()}

      {/* Breakdown Popup */}
      {showBreakdown && summary && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => setShowBreakdown(false)}>
          <div
            className="bg-[#161b22] border border-[#30363d] rounded-lg p-6 max-w-md w-full max-h-[80vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-[#e6edf3] text-sm font-semibold">Score Breakdown</h3>
              <button onClick={() => setShowBreakdown(false)} className="text-[#8b949e] hover:text-[#e6edf3] text-lg">&times;</button>
            </div>

            <div className="text-center mb-4">
              <span className="text-[#e6edf3] text-3xl font-bold font-mono">{totalPoints.toLocaleString()}</span>
              <span className="text-[#8b949e] text-sm ml-1">pts</span>
              {bonusPoints > 0 && (
                <div className="text-[10px] text-[#8b949e] font-mono mt-1">
                  {basePoints.toLocaleString()} earned
                  {' + '}
                  <span className="text-[#a371f7]">{bonusPoints.toLocaleString()} milestone bonus</span>
                </div>
              )}
            </div>

            <div className="space-y-2.5 mb-4">
              {Object.entries(TIER_LABELS).map(([key, cfg]) => (
                <ProgressBar
                  key={key}
                  value={s[key] || 0}
                  max={cfg.max}
                  color={cfg.color}
                  label={cfg.label}
                />
              ))}
            </div>

            <div className="border-t border-[#30363d] pt-3 mb-3">
              <p className="text-[10px] text-[#8b949e] font-mono uppercase tracking-wider mb-2">Tier Unlocks</p>
              <div className="grid grid-cols-2 gap-1.5">
                {['mcq', 'guided_4', 'guided_6', 'guided_9', 'mock_4', 'mock_6_9', 'mock_20'].map(tier => (
                  <div key={tier} className={`text-[10px] font-mono px-2 py-1 rounded flex items-center gap-1.5 ${
                    (s.unlockedTiers || []).includes(tier)
                      ? 'bg-[#3fb950]/10 text-[#3fb950]'
                      : 'bg-[#21262d] text-[#484f58]'
                  }`}>
                    <span>{(s.unlockedTiers || []).includes(tier) ? '✓' : '⬡'}</span>
                    <span>{tier.replace(/_/g, ' ')}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="border-t border-[#30363d] pt-3">
              <p className="text-[10px] text-[#8b949e] font-mono uppercase tracking-wider mb-2">Progress</p>
              <div className="flex justify-between text-xs">
                <span className="text-[#8b949e]">Today:</span>
                <span className="text-[#e6edf3] font-mono">{(s.todayPoints || 0).toLocaleString()} pts</span>
              </div>
              <div className="flex justify-between text-xs mt-1">
                <span className="text-[#8b949e]">Total:</span>
                <span className="text-[#e6edf3] font-mono">{totalPoints.toLocaleString()} / {totalTarget.toLocaleString()} pts</span>
              </div>
              <div className="flex justify-between text-xs mt-1">
                <span className="text-[#8b949e]">Day {daysElapsed} of {termLengthDays}:</span>
                <span className="text-[#e6edf3] font-mono">{pct}%</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Score History Panel ───────────────────────────────────────────────────────
export function ScoreHistory({ history, historyTotal, historyOffset, filter, onFilterChange, onLoadMore, loading }) {
  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center bg-[#0d1117]">
        <div className="text-[#8b949e] text-sm font-mono animate-pulse">Loading...</div>
      </div>
    );
  }

  const filteredHistory = filter === 'all'
    ? history
    : history.filter(h => h.mode === filter);

  return (
    <div className="flex-1 flex flex-col bg-[#0d1117] overflow-hidden">
      <div className="bg-[#161b22] border border-[#30363d] rounded-lg overflow-hidden flex flex-col flex-1 m-0 h-full">
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#30363d] flex-shrink-0">
          <h3 className="text-[#e6edf3] text-sm font-semibold">Points History</h3>
          <div className="flex gap-1 flex-wrap justify-end">
            {['all', 'mcq', 'guided', 'mock', 'time'].map(f => (
              <button
                key={f}
                onClick={() => onFilterChange(f)}
                className={`text-[10px] px-2 py-0.5 rounded font-mono transition-colors ${
                  filter === f
                    ? 'bg-[#58a6ff] text-[#0d1117] font-semibold'
                    : 'text-[#8b949e] hover:text-[#e6edf3]'
                }`}
              >
                {f === 'all' ? 'All' : MODE_LABELS[f] || f}
              </button>
            ))}
          </div>
        </div>

        <div className="overflow-y-auto flex-1">
          {filteredHistory.length === 0 ? (
            <div className="px-4 py-8 text-center text-[#484f58] text-xs italic">
              No points earned yet. Start answering questions!
            </div>
          ) : (
            <div className="divide-y divide-[#21262d]">
              {filteredHistory.map((entry, i) => (
                <div key={entry.id || i} className="px-4 py-2.5 flex items-center justify-between text-xs">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`inline-block w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                        entry.mode === 'mcq' ? 'bg-blue-400' :
                        entry.mode === 'guided' ? 'bg-violet-400' :
                        entry.mode === 'mock' ? 'bg-amber-400' :
                        entry.mode === 'time' ? 'bg-green-400' :
                        'bg-[#8b949e]'
                      }`} />
                      <span className="text-[#e6edf3] truncate">
                        {entry.questionId === '__time__' ? 'Active time' :
                         entry.questionId === '__streak__' ? 'Streak bonus' :
                         entry.questionId.startsWith('mcq_') ?
                           `MCQ — ${entry.questionId.split('_').slice(3, 4).join(' ')}` :
                           `${entry.archetype} — ${entry.questionId}`}
                      </span>
                    </div>
                    <div className="text-[10px] text-[#484f58] font-mono mt-0.5">
                      {REASON_LABELS[entry.reason] || entry.reason}
                      {' · '}
                      {new Date(entry.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>
                  <span className="text-[#3fb950] font-mono font-semibold flex-shrink-0 ml-3">
                    +{entry.points}
                  </span>
                </div>
              ))}
            </div>
          )}

          {history.length < historyTotal && (
            <div className="px-4 py-3 border-t border-[#30363d]">
              <button
                onClick={onLoadMore}
                className="w-full text-center text-xs text-[#58a6ff] hover:text-[#79c0ff] font-mono transition-colors"
              >
                Load more ({historyTotal - history.length} remaining)
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
